import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { usePageLoader } from "@/contexts/page-loader-context";
import { NavigationGuardProvider } from "@/contexts/navigation-guard-context";
import { useAuth } from "@/modules/auth/hooks/use-auth";
import { useBrandKitStore } from "@/modules/brand/stores/brand-kit-store";
import { useTags } from "@/modules/tag/hooks/use-tags";
import { useWallOfLoves } from "@/modules/wall-of-love/hooks/use-wall-of-loves";
import { WallOfLoveBuilder } from "@/modules/wall-of-love";
import type { WallOfLoveSaveContext } from "@/modules/wall-of-love/components/wall-of-love-builder";
import { useWallOfLoveTemplates } from "@/modules/wall-of-love-template";
import type { WallOfLoveTemplate } from "@/modules/wall-of-love-template";
import type { WallOfLove, WallOfLoveUpdateRequest } from "@/modules/wall-of-love/types";
import { useBrands, type Brand } from "@/modules/brand";

export default function WallOfLoveEditPage() {
	const { brandUrl, wallId } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { brandKit, view: fetchBrandKit } = useBrandKitStore();
	const { showWallOfLove, updateWallOfLove, syncStaticReviews, syncPinnedReviews } = useWallOfLoves();
	const { showWallOfLoveTemplate } = useWallOfLoveTemplates();
	const { tags, indexTags } = useTags();
	const { showBrand } = useBrands();
	const { showLoader, hideLoader } = usePageLoader();

	// Wall of Love data state
	const [wallData, setWallData] = useState<WallOfLove | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// Brand state - fetched directly since sidebar doesn't load on this page
	const [currentBrand, setCurrentBrand] = useState<Brand | null>(null);
	const [isBrandLoading, setIsBrandLoading] = useState(true);

	// Template schema state
	const [templateData, setTemplateData] = useState<WallOfLoveTemplate | null>(null);

	// Fetch wall data on mount if wallId exists
	useEffect(() => {
		const fetchWallData = async () => {
			if (currentWorkspaceId && wallId) {
				setIsLoading(true);
				try {
					const response = await showWallOfLove(wallId, currentWorkspaceId);
					if (response.data) {
						setWallData(response.data);
					}
				} catch (error) {
					console.error("Failed to fetch wall data:", error);
				} finally {
					setIsLoading(false);
				}
			}
		};
		fetchWallData();
	}, [currentWorkspaceId, wallId, showWallOfLove]);

	// Fetch template schema when wall data is loaded
	useEffect(() => {
		const fetchTemplateSchema = async () => {
			if (wallData?.template && wallData?.template_version) {
				try {
					const response = await showWallOfLoveTemplate(wallData.template, wallData.template_version);
					if (response.data) {
						setTemplateData(response.data);
					}
				} catch (error) {
					console.error("Failed to fetch template schema:", error);
				}
			}
		};
		fetchTemplateSchema();
	}, [wallData]);

	// Fetch tags on mount
	useEffect(() => {
		if (currentWorkspaceId) {
			indexTags(currentWorkspaceId);
		}
	}, [currentWorkspaceId, indexTags]);

	// Fetch brand kit on mount
	useEffect(() => {
		if (currentWorkspaceId) {
			fetchBrandKit(currentWorkspaceId);
		}
	}, [currentWorkspaceId, fetchBrandKit]);

	// Fetch brand by URL slug
	useEffect(() => {
		const fetchBrand = async () => {
			if (brandUrl) {
				setIsBrandLoading(true);
				try {
					const brand = await showBrand(brandUrl);
					if (brand) {
						setCurrentBrand(brand);
					}
				} catch (error) {
					console.error("Failed to fetch brand:", error);
				} finally {
					setIsBrandLoading(false);
				}
			}
		};
		fetchBrand();
	}, [brandUrl, showBrand]);

	// Handle page loader visibility based on brand loading state
	useEffect(() => {
		if (isBrandLoading) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [isBrandLoading, showLoader, hideLoader]);

	// Handle save with sequential API calls
	const handleSave = async (payload: WallOfLoveUpdateRequest, context: WallOfLoveSaveContext) => {
		if (!currentWorkspaceId || !wallId) return;
		try {
			// Step 1: Sync reviews FIRST (validation) - only if changed
			// This validates plan limits before committing wall settings
			if (context.wallType === "static" && context.staticReviewsChanged) {
				await syncStaticReviews(currentWorkspaceId, wallId, context.staticReviewIds);
			} else if (context.wallType === "dynamic" && context.pinnedReviewsChanged) {
				await syncPinnedReviews(currentWorkspaceId, wallId, context.pinnedReviewIds);
			}

			// Step 2: Update wall of love settings (only if sync succeeds)
			const response = await updateWallOfLove(wallId, currentWorkspaceId, payload);
			if (!response.data) {
				throw new Error("Failed to update wall of love");
			}

			// Step 3: Re-fetch canonical wall state from backend (source of truth)
			const refreshedWall = await showWallOfLove(wallId, currentWorkspaceId);
			if (refreshedWall.data) {
				setWallData(refreshedWall.data);
			}
		} catch (error) {
			console.error("Failed to save wall of love:", error);
			throw error;
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">Loading...</p>
			</div>
		);
	}

	if (!brandUrl) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">Invalid brand URL</p>
			</div>
		);
	}

	if (!currentWorkspaceId) {
		return (
			<div className="flex items-center justify-center h-screen">
				<p className="text-gray-500">No workspace selected</p>
			</div>
		);
	}

	// Show page loader while brand is loading
	if (isBrandLoading || !currentBrand) {
		return null;
	}

	return (
		<NavigationGuardProvider>
			<WallOfLoveBuilder
				currentBrand={currentBrand}
				brandUrl={brandUrl}
				brandKit={brandKit}
				tags={tags}
				wallData={wallData}
				templateData={templateData}
				workspaceId={currentWorkspaceId}
				onSave={handleSave}
			/>
		</NavigationGuardProvider>
	);
}
